/**
 * Assistant onboarding copy shown as the first chat message on Tech Doc / BRD pages.
 */
import { VALIDATION_LIMITS } from './validationLimits';

const MODULE_BRD = 'brd';

export const ONBOARDING_MESSAGE_ID = 'assistant-onboarding';

/** @typedef {'loading'|'loaded'|'error'|'signed_out'} QuotaStatus */

/**
 * @param {{ remaining?: number|null, limit?: number|null, used?: number|null, authenticated?: boolean, quotaStatus?: QuotaStatus, quotaError?: boolean }} quota
 */
export function formatRemainingAttemptsLine(quota = {}) {
  const status = quota.quotaStatus;

  if (status === 'loading') {
    return '• Remaining attempts: _loading…_';
  }
  if (quota.authenticated === false || status === 'signed_out') {
    return '• Remaining attempts: sign in to view your quota';
  }
  if (status === 'error' || quota.quotaError) {
    return '• Remaining attempts: unable to load quota (refresh the page or try again)';
  }
  if (status === 'loaded' && quota.remaining != null && quota.limit != null) {
    const remaining = Math.max(0, Number(quota.remaining));
    const cap = Number(quota.limit);
    return `• Remaining attempts: **${remaining} / ${cap}**`;
  }
  if (quota.remaining != null && quota.limit != null) {
    const remaining = Math.max(0, Number(quota.remaining));
    const cap = Number(quota.limit);
    return `• Remaining attempts: **${remaining} / ${cap}**`;
  }
  return '• Remaining attempts: unable to load quota';
}

/**
 * @param {'tech-doc'|'brd'} moduleType
 * @param {{ remaining?: number|null, limit?: number|null, authenticated?: boolean, quotaStatus?: QuotaStatus }} quota
 */
export function buildOnboardingMarkdown(moduleType, quota = {}) {
  const defaultLimit =
    moduleType === MODULE_BRD
      ? VALIDATION_LIMITS.brdMaxGenerationsPerUser
      : VALIDATION_LIMITS.techDocMaxGenerationsPerUser;

  const remainingLine = formatRemainingAttemptsLine({
    ...quota,
    limit: quota.limit ?? defaultLimit,
  });

  if (moduleType === MODULE_BRD) {
    return [
      'Hello! I’m your BRD documentation assistant.',
      '',
      'I can help you generate Business Requirements Documents for Oracle Forms modernization projects.',
      '',
      '**📄 BRD Generation**',
      '',
      '• Create Business Requirements Documents for Oracle Forms(BRD).',
      '',
      remainingLine,
    ].join('\n');
  }

  return [
    'Hello! I’m your intelligent documentation assistant. I can help you transform your inputs into structured, high-quality documents.',
    '',
    'Here’s what I can do for you:',
    '',
    '**📄 Document Generation**',
    '',
    '• Generate Technical Documentation.',
    '• Produce Architecture documentation.',
    '• Generate Functional Documentation.',
    '• Generate Data model diagram.',
    '• Generate Database Schema Report.',
    '• Generate Reverse Engineering Summary.',
    '',
    remainingLine,
  ].join('\n');
}

export function createOnboardingChatMessage(moduleType, quota = {}) {
  return {
    id: ONBOARDING_MESSAGE_ID,
    type: 'ai',
    isOnboarding: true,
    content: buildOnboardingMarkdown(moduleType, quota),
    timestamp: new Date(),
  };
}
